import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const FREQUENCIES = ["weekly", "monthly", "quarterly", "six_monthly", "annual", "custom"];

export default function AuditScheduleTab({ canManage, refreshTick, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [sites, setSites] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/audits-qa/schedule");
      setRows(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshTick]);
  useEffect(() => {
    api.get("/audits-qa/templates", { params: { active_only: true } }).then((r) => setTemplates(r.data.items || [])).catch(() => {});
    api.get("/sites").then((r) => setSites(r.data || [])).catch(() => {});
  }, []);

  const del = async (id) => {
    if (!window.confirm("Remove this scheduled audit?")) return;
    try { await api.delete(`/audits-qa/schedule/${id}`); toast.success("Schedule removed"); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-lg" data-testid="audits-qa-schedule-tab">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-base font-semibold text-slate-900 flex items-center gap-2"><CalendarClock className="w-4 h-4 text-sky-700" /> Audit Schedule</h2>
          <p className="text-xs text-slate-500 mt-0.5">Recurring audits by site with automatic next-due tracking.</p>
        </div>
        {canManage && <Button size="sm" onClick={() => setCreating(true)} data-testid="btn-schedule-new" className="h-8 bg-sky-700 hover:bg-sky-800"><Plus className="w-3.5 h-3.5 mr-1.5" /> Schedule Audit</Button>}
      </div>
      {loading ? <div className="p-8 text-sm text-slate-500">Loading…</div>
        : rows.length === 0 ? <div className="p-16 text-center text-sm text-slate-500">No scheduled audits. Set up a recurring cadence to keep on top of your programme.</div>
        : (
        <div className="overflow-x-auto">
          <table className="data-table w-full" data-testid="audits-schedule-table">
            <thead><tr>
              <th>Template / Category</th><th>Site</th><th>Frequency</th><th>Responsible</th>
              <th>Last Completed</th><th>Next Due</th><th>Status</th><th className="w-20 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => {
                const site = sites.find((s) => s.id === r.site_id);
                return (
                  <tr key={r.id} data-testid={`schedule-row-${r.id}`}>
                    <td><div className="font-medium text-slate-900">{r.template_name || r.category_label}</div>
                      {r.template_name && <div className="text-[11px] text-slate-500">{r.category_label}</div>}
                    </td>
                    <td className="text-slate-500">{site?.name || <span className="text-slate-300">—</span>}</td>
                    <td className="capitalize text-slate-700">{r.frequency?.replace(/_/g, " ")}</td>
                    <td className="text-slate-700">{r.responsible_name || "—"}</td>
                    <td className="tabular-nums">{r.last_completed || "—"}</td>
                    <td className="tabular-nums">{r.next_due_date || "—"}</td>
                    <td>
                      {r.is_overdue ? <span className="inline-flex text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border bg-red-100 text-red-800 border-red-200">Overdue</span>
                        : <span className="inline-flex text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border bg-emerald-100 text-emerald-800 border-emerald-200">On track</span>}
                    </td>
                    <td className="text-right whitespace-nowrap">
                      {canManage && <>
                        <button onClick={() => setEditing(r)} className="p-1 text-slate-500 hover:text-sky-700" data-testid={`btn-edit-schedule-${r.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => del(r.id)} className="p-1 text-slate-500 hover:text-red-600 ml-0.5" data-testid={`btn-delete-schedule-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <ScheduleDialog
        open={creating || !!editing} schedule={editing}
        onOpenChange={(v) => { if (!v) { setEditing(null); setCreating(false); } }}
        onSaved={() => { setEditing(null); setCreating(false); load(); onChanged?.(); }}
        templates={templates} sites={sites}
      />
    </section>
  );
}

function ScheduleDialog({ open, schedule, onOpenChange, onSaved, templates, sites }) {
  const [form, setForm] = useState(defaults());
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ ...defaults(), ...(schedule || {}) }); }, [open, schedule]);
  const patch = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const pickTemplate = (id) => {
    patch("template_id", id || null);
    const t = templates.find((x) => x.id === id);
    if (t) { patch("template_name", t.name); patch("category", t.category); }
  };
  const save = async () => {
    if (!form.category) return toast.error("Category required");
    setSaving(true);
    try {
      if (schedule?.id) await api.patch(`/audits-qa/schedule/${schedule.id}`, form);
      else await api.post("/audits-qa/schedule", form);
      toast.success(schedule?.id ? "Schedule updated" : "Schedule created");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-white" data-testid="schedule-dialog">
        <DialogHeader><DialogTitle>{schedule?.id ? "Edit Scheduled Audit" : "Schedule an Audit"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <FL label="Template">
              <select value={form.template_id || ""} onChange={(e) => pickTemplate(e.target.value)} data-testid="schedule-input-template" className="h-9 w-full text-sm border border-slate-300 rounded-md bg-white px-2">
                <option value="">— Choose a template —</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </FL>
          </div>
          <FL label="Frequency">
            <select value={form.frequency} onChange={(e) => patch("frequency", e.target.value)} data-testid="schedule-input-frequency" className="h-9 w-full text-sm border border-slate-300 rounded-md bg-white px-2 capitalize">
              {FREQUENCIES.map((f) => <option key={f} value={f}>{f.replace(/_/g, " ")}</option>)}
            </select>
          </FL>
          {form.frequency === "custom" && <FL label="Every N days"><Input type="number" min="1" value={form.custom_days ?? ""} onChange={(e) => patch("custom_days", Number(e.target.value))} data-testid="schedule-input-custom" /></FL>}
          <FL label="Site">
            <select value={form.site_id || ""} onChange={(e) => patch("site_id", e.target.value || null)} data-testid="schedule-input-site" className="h-9 w-full text-sm border border-slate-300 rounded-md bg-white px-2">
              <option value="">— Choose site —</option>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </FL>
          <FL label="Responsible person"><Input value={form.responsible_name || ""} onChange={(e) => patch("responsible_name", e.target.value)} data-testid="schedule-input-responsible" /></FL>
          <FL label="Last completed"><Input type="date" value={form.last_completed || ""} onChange={(e) => patch("last_completed", e.target.value || null)} data-testid="schedule-input-last" /></FL>
          <FL label="Next due (optional)"><Input type="date" value={form.next_due_date || ""} onChange={(e) => patch("next_due_date", e.target.value || null)} data-testid="schedule-input-next" /></FL>
          <div className="col-span-2"><FL label="Notes"><Textarea rows={2} value={form.notes || ""} onChange={(e) => patch("notes", e.target.value)} /></FL></div>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-sky-700 hover:bg-sky-800" data-testid="btn-schedule-save">{saving ? "Saving…" : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function defaults() {
  return { template_id: null, template_name: "", category: "other", frequency: "monthly",
           custom_days: null, site_id: null, responsible_name: "", last_completed: null,
           next_due_date: null, notes: "", is_active: true };
}
function FL({ label, children }) { return <label className="block"><span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span><div className="mt-0.5">{children}</div></label>; }
